import CookieService from 'Services/Cookie/CookieService'
import { makeAutoObservable } from 'mobx'
import { cloneDeep, get } from 'lodash'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'

const MILLIS_IN_HOUR = 60 * 60 * 1000
const REMEMBER_REFERRAL_COOKIE_KEY_PREFIX = 'fg_remember_referral_code_'

const rememberReferralCookieService = new CookieService()

class StoreRememberReferralCode {

    referralRememberSettings = {
        rememberHours: 0,
        latestUpdateUTCZero: null,
    }

    referralRememberCode = ''

    constructor() {
        makeAutoObservable(this)
    }

    GetRememberReferralCookie() {

        const cookieKey = `${REMEMBER_REFERRAL_COOKIE_KEY_PREFIX}${storeCountry.Country3()}`
        const rawCookie = rememberReferralCookieService.get(cookieKey)

        if (!rawCookie) {
            return null
        }

        try {
            return JSON.parse(rawCookie)
        } catch (error) {
            return null
        }
    }

    SetRememberReferralCookie(payload, expiresAt) {
        const cookieKey = `${REMEMBER_REFERRAL_COOKIE_KEY_PREFIX}${storeCountry.Country3()}`
        rememberReferralCookieService.set(cookieKey, JSON.stringify(payload), {
            expires: new Date(expiresAt)
        })
    }

    ClearRememberReferralCookie() {
        const cookieKey = `${REMEMBER_REFERRAL_COOKIE_KEY_PREFIX}${storeCountry.Country3()}`
        rememberReferralCookieService.remove(cookieKey)
    }

    SetReferralSettings(referralSettings) {
        referralSettings = referralSettings || {}

        this.referralRememberSettings = {
            rememberHours: get(referralSettings, 'rememberHours', 0),
            latestUpdateUTCZero: get(referralSettings, 'latestUpdateUTCZero', null),
        }
    }

    CheckNewSettingVersion() {
        const referralRememberCookie = this.GetRememberReferralCookie()
        const currentReferralRememberSettings = this.referralRememberSettings

        if (!referralRememberCookie) {
            return true
        }

        const referralRememberSettingsFromCookie = get(referralRememberCookie, 'referralRememberSettings', {})

        const latestUpdateUTCZeroFromCookie = get(referralRememberSettingsFromCookie, 'latestUpdateUTCZero', null)

        const versionTimestampCookie = Date.parse(latestUpdateUTCZeroFromCookie)
        const versionTimestampCurrent = Date.parse(currentReferralRememberSettings.latestUpdateUTCZero)

        return versionTimestampCookie !== versionTimestampCurrent
    }

    CheckRequireRememberReferralCode(referralCode) {
        const rememberHours = Number(this.referralRememberSettings?.rememberHours || 0)
        return referralCode && rememberHours > 0
    }

    HandleNewReferralCode(referralCode) {

        this.ClearRememberReferralCookie()

        referralCode = String(referralCode).trim()

        if (!this.CheckRequireRememberReferralCode(referralCode)) {
            return
        }

        const now = new Date()

        const nowUTCZero = now.toISOString()

        const rememberHours = Number(this.referralRememberSettings?.rememberHours || 0)

        const expiresTimeAt = now.getTime() + (rememberHours * MILLIS_IN_HOUR)

        const expiresAtUTCZero = new Date(expiresTimeAt).toISOString()

        const cookieData = {
            referralCode,
            expireAtUTCZero: expiresAtUTCZero,
            startAtUTCZero: nowUTCZero,
            referralRememberSettings: cloneDeep(this.referralRememberSettings),
        }

        this.SetRememberReferralCookie(cookieData, expiresAtUTCZero)

    }

    CheckExpiredRememberReferralCookie() {

        const cookieData = this.GetRememberReferralCookie()
        if (!cookieData) {
            return false
        }
        const nowUTCZeroTime = Date.now()
        const expireAtUTCZeroTime = Date.parse(cookieData.expireAtUTCZero)

        return expireAtUTCZeroTime < nowUTCZeroTime
    }

    /**
     * @returns {ReferralCode} String | null
     */
    HandleProcessGetRememberReferralCode() {

        // 0. Check Is there setting from API ? If not, return null
        const rememberHours = Number(this.referralRememberSettings?.rememberHours || 0)
        if (rememberHours <= 0) return null

        // 1. Check if the cookie is disappear ? If not, return null
        const cookieData = this.GetRememberReferralCookie()
        if (!cookieData) return null

        // 2. Check version and duration, if version is different, use new duration
        const isVersionDifferent = this.CheckNewSettingVersion()

        let useExpireAtUTCZero = cookieData.expireAtUTCZero
        const startAtUTCZero = cookieData.startAtUTCZero
        const startAtUTCZeroTime = Date.parse(startAtUTCZero)

        if (isVersionDifferent) {
            // 3. Set new expireAtUTCZero with new duration (startAtUTCZero + new duration) 
            //    and set Cookie data with new setting and new expireAtUTCZero
            //    but use old startAtUTCZero and old referralCode
            const newDuration = rememberHours * MILLIS_IN_HOUR
            useExpireAtUTCZero = new Date(startAtUTCZeroTime + newDuration).toISOString()

            const newCookieData = {
                referralCode: cookieData.referralCode,
                expireAtUTCZero: useExpireAtUTCZero,
                startAtUTCZero: startAtUTCZero,
                referralRememberSettings: this.referralRememberSettings,
            }
            this.SetRememberReferralCookie(newCookieData, useExpireAtUTCZero)
        }
        // 4. Check The new expireAtUTCZero is expired ? If yes, return null
        const expireAtUTCZeroTime = Date.parse(useExpireAtUTCZero)
        if (expireAtUTCZeroTime < Date.now()) {
            this.ClearRememberReferralCookie()
            return null
        }

        // 5. Return referral code from cookie
        return cookieData.referralCode
    }

}

const storeRememberReferralCode = new StoreRememberReferralCode()

export default storeRememberReferralCode